import React, { useContext } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import { ReactComponent as UnlockFill } from "bootstrap-icons/icons/unlock-fill.svg";
import Spinner from "../spinner/Spinner";
import { useHeadOfficeById, useHeadOfficeDashboard, useToggleHeadOfficeStatus } from "../../hooks/useHeadOffice";
import Swal from "sweetalert2";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";
import { AuthContext } from "../../AuthProvider/AuthProvider";

const HeadOfficeDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const headOfficeId = searchParams.get("id");

  const { data: detailsData, isLoading: detailsLoading, error: detailsError } = useHeadOfficeById(headOfficeId);
  const { data: dashboardData, isLoading: dashboardLoading, error: dashboardError } = useHeadOfficeDashboard(headOfficeId);
  const toggleStatusMutation = useToggleHeadOfficeStatus();

  const { permissions } = useContext(AuthContext);

  const headOffice = detailsData?.data;
  const dashboard = dashboardData?.data;

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/head-office/list")}
              className="text-orange1 hover:text-orange-600"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold">{"Office Details"}</h1>
            {headOffice && (
              <>
                <span style={{
                  padding: "2px 10px",
                  borderRadius: "9999px",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  backgroundColor: headOffice.is_activated ? "#dbeafe" : "#f3f4f6",
                  color: headOffice.is_activated ? "#1e40af" : "#4b5563",
                }}>
                  {headOffice.is_activated ? "Activated" : "Not Activated"}
                </span>
              </>
            )}
          </div>
          <div className="flex gap-2">
            {/* <button
              onClick={handleToggleActivation}
              className={`flex items-center gap-2 px-4 py-2 rounded transition ${
                headOffice.is_activated
                  ? "bg-red-100 text-red-700 hover:bg-red-200"
                  : "bg-green-100 text-green-700 hover:bg-green-200"
              }`}
              disabled={toggleActivationMutation.isLoading}
            >
              {headOffice.is_activated ? <ToggleOff className="w-5 h-5" /> : <ToggleOn className="w-5 h-5" />}
              {headOffice.is_activated ? "Deactivate" : "Activate"}
            </button>
             <button
              onClick={handleResetPassword}
              className="flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded hover:bg-purple-200 transition"
              disabled={resetPasswordMutation.isLoading}
            >
              <KeyFill className="w-5 h-5" />
              Reset Password
            </button> */}
            {headOffice?.status === "suspended" && (
              <button
                onClick={async () => {
                  const result = await Swal.fire({
                    title: "Unsuspend Head Office",
                    text: `Are you sure you want to unsuspend "${headOffice?.name}"?`,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#f97316",
                    cancelButtonColor: "#6b7280",
                    confirmButtonText: "Yes, unsuspend!",
                    cancelButtonText: "Cancel",
                  });
                  if (result.isConfirmed) {
                    toggleStatusMutation.mutate(headOfficeId);
                  }
                }}
                disabled={toggleStatusMutation.isLoading}
                className="flex items-center gap-2 bg-orange1 text-white px-4 py-2 rounded hover:bg-orange-600 transition disabled:opacity-50"
              >
                <UnlockFill className="w-5 h-5" />
                {toggleStatusMutation.isLoading ? "Updating..." : "Unsuspend"}
              </button>
            )}
          </div>
        </div>

        {detailsLoading || dashboardLoading ? (
          <Spinner loading={detailsLoading || dashboardLoading} />
        ) : detailsError || !headOffice ? (
          <div className="text-red-500 bg-red-50 border border-red-200 rounded p-4">
            <h3 className="font-bold mb-2">Error loading head office</h3>
            <p>{detailsError?.response?.data?.message || "Head office not found"}</p>
            <Link to="/head-office/list" className="text-blue-600 hover:underline mt-2 inline-block">
              Back to list
            </Link>
          </div>
        ) : (
          <>
            {/* Dashboard Stats */}
            {dashboard && !dashboardError && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div className="bg-white p-6 rounded-lg shadow">
                  <p className="text-gray-500 text-sm">Total Branches</p>
                  <h3 className="text-2xl font-bold mt-2">{dashboard.total_branches || 0}</h3>
                  <p className="text-sm text-green-600 mt-1">
                    {dashboard.active_branches || 0} active
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                  <p className="text-gray-500 text-sm">Inventory Value</p>
                  <h3 className="text-2xl font-bold mt-2">
                    <CurrencyFormatter amount={dashboard.total_inventory_value || 0} />
                  </h3>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                  <p className="text-gray-500 text-sm">Low Stock Items</p>
                  <h3 className="text-2xl font-bold mt-2 text-yellow-600">
                    {dashboard.low_stock_items || 0}
                  </h3>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                  <p className="text-gray-500 text-sm">Out of Stock</p>
                  <h3 className="text-2xl font-bold mt-2 text-red-600">
                    {dashboard.out_of_stock_items || 0}
                  </h3>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 border-b pb-2">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Email</label>
                  <p className="font-medium">{headOffice.email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Phone Number</label>
                  <p className="font-medium">{headOffice.phone_number}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">State</label>
                  <p className="font-medium">{headOffice.state}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">City</label>
                  <p className="font-medium">{headOffice.city}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-500">Address</label>
                  <p className="font-medium">{headOffice.address}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Two Factor Enabled</label>
                  <p className="font-medium">{headOffice.two_factor_enabled ? "Yes" : "No"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Status</label>
                  <div className="mt-1">
                    <span style={{
                      padding: "2px 10px",
                      borderRadius: "9999px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textTransform: "capitalize",
                      backgroundColor: headOffice.status === "active" ? "#dcfce7" : headOffice.status === "suspended" ? "#fef9c3" : "#fee2e2",
                      color: headOffice.status === "active" ? "#166534" : headOffice.status === "suspended" ? "#854d0e" : "#991b1b",
                    }}>
                      {headOffice.status || "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Information */}
            {dashboard && !dashboardError && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-semibold mb-4 border-b pb-2">Stock Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Total Received</label>
                    <p className="font-medium text-lg">{dashboard.total_received || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Total Allocated</label>
                    <p className="font-medium text-lg">{dashboard.total_allocated || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Pending Requests</label>
                    <p className="font-medium text-lg">{dashboard.pending_requests || 0}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 border-b pb-2">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link
                  to={`/head-office/branches?id=${headOfficeId}`}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-orange1 hover:bg-orange-50 transition text-center"
                >
                  <h3 className="font-semibold">View Branches</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Manage {headOffice.total_branches} branches
                  </p>
                </Link>
                <Link
                  to={`/head-office/inventory?id=${headOfficeId}`}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-orange1 hover:bg-orange-50 transition text-center"
                >
                  <h3 className="font-semibold">View Inventory</h3>
                  <p className="text-sm text-gray-500 mt-1">Manage stock levels</p>
                </Link>
                {permissions.some(permission => permission.name === 'head_office.allocate_stock') && (
                  <Link
                    to={`/head-office/allocate-stock?id=${headOfficeId}`}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-orange1 hover:bg-orange-50 transition text-center"
                  >
                    <h3 className="font-semibold">Allocate Stock</h3>
                    <p className="text-sm text-gray-500 mt-1">Add inventory items</p>
                  </Link>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayouts>
  );
};

export default HeadOfficeDetails;
